import { useState } from "react";
import { useNavigate, Link } from "react-router";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "../../../auth";
import { signInPatient } from "../../application/portalAccount";

export default function PortalLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signInPatient(email, password);
      navigate("/portal", { replace: true });
    } catch {
      setError(t("portal.login.error", "Incorrect email or password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      panelTagline={t("portal.login.panelTagline")}
      panelTitle={t("portal.login.panelTitle")}
      colorScheme="portal"
    >
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-content-primary mb-2">
            {t("portal.login.title")}
          </h1>
          <p className="text-content-tertiary">
            {t("portal.login.subtitle")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-content-secondary">
              {t("portal.login.emailLabel")}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder={t("portal.login.emailPlaceholder")}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-content-secondary">
                {t("portal.login.passwordLabel")}
              </label>
              <Link
                to="/portal/forgot-password"
                className="text-xs font-semibold text-brand-600 dark:text-brand-400 hover:underline"
              >
                {t("portal.login.forgotPassword")}
              </Link>
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder={t("portal.login.passwordPlaceholder")}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !email || !password}
            className="w-full rounded-xl bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold py-3 transition-colors"
          >
            {loading ? t("portal.login.loading") : t("portal.login.submit")}
          </button>
        </form>

        <div className="pt-4 border-t border-gray-100 dark:border-white/[0.05]">
          <p className="text-sm font-medium text-center text-content-tertiary">
            {t("portal.login.isTherapist")}{" "}
            <Link
              to="/login"
              className="text-brand-600 dark:text-brand-400 font-bold hover:underline ml-1"
            >
              {t("portal.login.goToTherapistLogin")}
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
